import { useEffect, useId, useState } from 'react';
import styled from '@emotion/styled';
import { colors as themeColors } from 'src/theme';
import { CheckIcon } from 'src/components';
import { ValidationErrorStyle } from 'src/common/style/validationError.style';
import { errorIdFor, focusRing, onEnterKey, visuallyHidden } from 'src/common/style/a11y.style';

const Wrapper = styled.div({
  position: 'relative',
});

const Container = styled.label<{ left?: boolean; disabled: boolean }>(({ left, disabled }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
  flexDirection: left ? 'row-reverse' : 'row',
  cursor: disabled ? 'not-allowed' : 'pointer',
}));

const TextContainer = styled.span({
  display: 'block',
  flex: 1,
});

const HiddenInput = styled.input({
  ...visuallyHidden,
  '&:focus-visible + span': focusRing,
});

const CheckboxBox = styled.span<{ isSelected: boolean; disabled: boolean }>(({ isSelected, disabled }) => ({
  width: '20px',
  height: '20px',
  flexShrink: 0,
  boxSizing: 'border-box',
  border: `2px solid ${isSelected ? themeColors.primary1 : themeColors.text4}`,
  backgroundColor: isSelected ? themeColors.primary1 : themeColors.white,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease-in-out',
}));

const LabelText = styled.span<{ disabled: boolean }>(({ disabled }) => ({
  display: 'inline-block',
  color: disabled ? themeColors.text4 : themeColors.text1,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '22px',
}));

// In normal flow so the message does not overlap the next checkbox
const ErrorText = styled(ValidationErrorStyle)({
  position: 'static',
});

const DescriptionText = styled.span({
  display: 'block',
  fontSize: '12px',
  color: themeColors.text4,
  lineHeight: '16px',
});

export type CheckboxInputProps = {
  id?: string;
  label?: string;
  description?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  left?: boolean;
  labelComponent?: React.ReactNode;
  // Accessible name when the checkbox has no visible label next to it
  ariaLabel?: string;
};

export const CheckboxInput: React.FC<CheckboxInputProps> = ({
  id,
  label,
  description,
  value = false,
  onChange,
  disabled = false,
  required = false,
  error = '',
  className,
  left = false,
  labelComponent,
  ariaLabel,
}) => {
  const [isSelected, setIsSelected] = useState<boolean>(value);
  const generatedId = useId();
  const inputId = id || generatedId;
  const errorId = errorIdFor(inputId);

  useEffect(() => {
    setIsSelected(value);
  }, [value]);

  const handleToggle = () => {
    if (disabled) return;
    const newValue = !isSelected;
    setIsSelected(newValue);
    if (onChange) onChange(newValue);
  };

  const hasText = !!(label || labelComponent || description);

  return (
    <Wrapper className={className}>
      <Container left={left} disabled={disabled}>
        {hasText && (
          <TextContainer>
            {label && !labelComponent && <LabelText disabled={disabled}>{label}</LabelText>}
            {labelComponent && labelComponent}
            {description && <DescriptionText>{description}</DescriptionText>}
          </TextContainer>
        )}
        <HiddenInput
          type="checkbox"
          id={inputId}
          checked={isSelected}
          onChange={handleToggle}
          onKeyDown={onEnterKey(handleToggle)}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        <CheckboxBox isSelected={isSelected} disabled={disabled} aria-hidden="true">
          {isSelected && <CheckIcon color={themeColors.white} width={18} height={18} />}
        </CheckboxBox>
      </Container>
      {error && <ErrorText id={errorId}>{error}</ErrorText>}
    </Wrapper>
  );
};
